/**
 * Willtec Product Gallery Custom Element
 * Single unified state (activeIndex) controlling:
 * - Desktop Thumbnails (with smooth scroll into view)
 * - Desktop Arrow Navigation (previous / next on main image)
 * - Desktop Interactive Zoom (pointer tracking, scale 2x, boundary clamping, requestAnimationFrame)
 * - Mobile Touch/Swipe gestures (with zero pointer interference)
 * - Mobile Dots Windowing & Navigation
 * - Variant Media Synchronization
 * - Keyboard Navigation
 */
(() => {
  if (customElements.get('willtec-product-gallery')) return;

  class WilltecProductGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = this.querySelectorAll('[data-gallery-slide]');
    this.thumbs = this.querySelectorAll('[data-gallery-thumb]');
    this.thumbsContainer = this.querySelector('[data-gallery-thumbnails]');
    this.dots = this.querySelectorAll('[data-gallery-dot]');
    this.prevBtns = this.querySelectorAll('[data-gallery-prev]');
    this.nextBtns = this.querySelectorAll('[data-gallery-next]');
    this.dotsContainer = this.querySelector('[data-gallery-dots]');
    this.zoomHint = this.querySelector('[data-gallery-zoom-hint]');

    this.total = this.slides.length;
    if (this.total === 0) return;

    this.activeIndex = 0;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;

    // Zoom state
    this.isZoomActive = false;
    this.zoomRafId = null;

    this.initEvents();
    this.initZoom();
    this.update();
  }

  disconnectedCallback() {
    this.resetZoom();
  }

  initEvents() {
    // 1. Thumbnails Click
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = parseInt(thumb.getAttribute('data-index'), 10);
        if (!isNaN(idx)) this.setActiveIndex(idx);
      });
    });

    // 2. Dots Click
    this.dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const idx = parseInt(dot.getAttribute('data-index'), 10);
        if (!isNaN(idx)) this.setActiveIndex(idx);
      });
    });

    // 3. Arrow Controls (Desktop + Mobile shared handlers)
    this.prevBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActiveIndex(this.activeIndex - 1);
      });
    });

    this.nextBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.setActiveIndex(this.activeIndex + 1);
      });
    });

    // 4. Touch / Swipe Gestures (Mobile only)
    if (this.viewport) {
      this.viewport.addEventListener(
        'touchstart',
        (e) => {
          this.touchStartX = e.touches[0].clientX;
          this.isDragging = true;
        },
        { passive: true }
      );

      this.viewport.addEventListener(
        'touchend',
        (e) => {
          if (!this.isDragging) return;
          this.touchEndX = e.changedTouches[0].clientX;
          this.isDragging = false;
          this.handleSwipe();
        },
        { passive: true }
      );
    }

    // 5. Keyboard Navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.setActiveIndex(this.activeIndex - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.setActiveIndex(this.activeIndex + 1);
      }
    });

    // 6. External Event for Variant Switch Sync
    document.addEventListener('willtec:gallery:select-media', (e) => {
      if (e.detail && e.detail.mediaId) {
        this.selectByMediaId(e.detail.mediaId);
      }
    });
  }

  initZoom() {
    // Only enable hover zoom on devices with mouse/fine pointer
    const supportsFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!supportsFinePointer || !this.viewport) return;

    this.viewport.addEventListener('pointerenter', (e) => {
      // Don't zoom if pointer enters directly on top of controls (wishlist, share, arrows)
      if (e.target.closest('button, a, [data-gallery-prev], [data-gallery-next]')) return;
      this.handlePointerEnter();
    });

    this.viewport.addEventListener('pointermove', (e) => {
      // If hovering over controls, reset zoom to keep product image calm
      if (e.target.closest('button, a, [data-gallery-prev], [data-gallery-next]')) {
        this.resetZoom();
        return;
      }
      this.handlePointerMove(e);
    });

    this.viewport.addEventListener('pointerleave', () => {
      this.resetZoom();
    });
  }

  handlePointerEnter() {
    const activeSlide = this.slides[this.activeIndex];
    if (!activeSlide || activeSlide.getAttribute('data-media-type') !== 'image') return;

    const img = activeSlide.querySelector('[data-gallery-image]');
    if (!img) return;

    this.isZoomActive = true;
    img.style.willChange = 'transform';
    img.style.transform = 'scale(2)';
    this.viewport.classList.add('is-zoomed');

    if (this.zoomHint) {
      this.zoomHint.classList.add('opacity-0');
    }
  }

  handlePointerMove(e) {
    const activeSlide = this.slides[this.activeIndex];
    if (!activeSlide || activeSlide.getAttribute('data-media-type') !== 'image') return;

    const img = activeSlide.querySelector('[data-gallery-image]');
    if (!img) return;

    if (!this.isZoomActive) {
      this.handlePointerEnter();
    }

    if (this.zoomRafId) {
      cancelAnimationFrame(this.zoomRafId);
    }

    this.zoomRafId = requestAnimationFrame(() => {
      const rect = this.viewport.getBoundingClientRect();
      const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
      const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

      const xPercent = (x / rect.width) * 100;
      const yPercent = (y / rect.height) * 100;

      img.style.transformOrigin = `${xPercent.toFixed(2)}% ${yPercent.toFixed(2)}%`;
    });
  }

  resetZoom() {
    if (this.zoomRafId) {
      cancelAnimationFrame(this.zoomRafId);
      this.zoomRafId = null;
    }

    this.isZoomActive = false;

    this.slides.forEach((slide) => {
      const img = slide.querySelector('[data-gallery-image]');
      if (img) {
        img.style.transform = '';
        img.style.transformOrigin = '';
        img.style.willChange = '';
      }
    });

    if (this.viewport) {
      this.viewport.classList.remove('is-zoomed');
    }

    if (this.zoomHint) {
      this.zoomHint.classList.remove('opacity-0');
    }
  }

  handleSwipe() {
    const diffX = this.touchStartX - this.touchEndX;
    const threshold = 40; // 40px drag threshold

    if (Math.abs(diffX) > threshold) {
      if (diffX > 0) {
        this.setActiveIndex(this.activeIndex + 1);
      } else {
        this.setActiveIndex(this.activeIndex - 1);
      }
    }
  }

  selectByMediaId(mediaId) {
    const targetSlide = Array.from(this.slides).find((s) => s.getAttribute('data-media-id') === String(mediaId));
    if (targetSlide) {
      const idx = parseInt(targetSlide.getAttribute('data-index'), 10);
      if (!isNaN(idx)) this.setActiveIndex(idx);
    }
  }

  setActiveIndex(index) {
    // Immediate reset of zoom before switching media
    this.resetZoom();

    if (index < 0) index = 0;
    if (index >= this.total) index = this.total - 1;

    this.activeIndex = index;
    this.update();
  }

  update() {
    // 1. Move Track
    if (this.track) {
      const offset = -this.activeIndex * 100;
      this.track.style.transform = `translateX(${offset}%)`;
    }

    // 2. Sync Desktop Thumbnails
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isHorizontal = this.getAttribute('data-thumbnails-position') === 'bottom';

    this.thumbs.forEach((thumb, idx) => {
      if (idx === this.activeIndex) {
        thumb.setAttribute('aria-current', 'true');
        if (this.thumbsContainer) {
          if (isHorizontal) {
            if (this.activeIndex === 0) {
              this.thumbsContainer.scrollTo({ left: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
            } else if (this.activeIndex === this.total - 1) {
              this.thumbsContainer.scrollTo({ left: this.thumbsContainer.scrollWidth, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
            } else {
              const thumbRect = thumb.getBoundingClientRect();
              const containerRect = this.thumbsContainer.getBoundingClientRect();
              if (thumbRect.left < containerRect.left) {
                this.thumbsContainer.scrollBy({
                  left: thumbRect.left - containerRect.left - 8,
                  behavior: prefersReducedMotion ? 'auto' : 'smooth'
                });
              } else if (thumbRect.right > containerRect.right) {
                this.thumbsContainer.scrollBy({
                  left: thumbRect.right - containerRect.right + 8,
                  behavior: prefersReducedMotion ? 'auto' : 'smooth'
                });
              }
            }
          } else {
            if (this.activeIndex === 0) {
              this.thumbsContainer.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
            } else if (this.activeIndex === this.total - 1) {
              this.thumbsContainer.scrollTo({ top: this.thumbsContainer.scrollHeight, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
            } else {
              const thumbRect = thumb.getBoundingClientRect();
              const containerRect = this.thumbsContainer.getBoundingClientRect();
              if (thumbRect.top < containerRect.top) {
                this.thumbsContainer.scrollBy({
                  top: thumbRect.top - containerRect.top - 4,
                  behavior: prefersReducedMotion ? 'auto' : 'smooth'
                });
              } else if (thumbRect.bottom > containerRect.bottom) {
                this.thumbsContainer.scrollBy({
                  top: thumbRect.bottom - containerRect.bottom + 4,
                  behavior: prefersReducedMotion ? 'auto' : 'smooth'
                });
              }
            }
          }
        } else {
          thumb.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'nearest'
          });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    // 3. Sync Mobile Dots (Padrão Hero Slider)
    this.dots.forEach((dot, idx) => {
      if (idx === this.activeIndex) {
        dot.setAttribute('aria-current', 'true');
        dot.classList.remove('w-2.5', 'bg-black/30', 'hover:bg-black/60');
        dot.classList.add('w-6', 'bg-[#FE3B40]');
      } else {
        dot.removeAttribute('aria-current');
        dot.classList.remove('w-6', 'bg-[#FE3B40]');
        dot.classList.add('w-2.5', 'bg-black/30', 'hover:bg-black/60');
      }
    });

    // 4. Mobile Dots Windowing (max 5 dots visible if many images)
    if (this.dotsContainer && this.total > 5) {
      let startDot = Math.max(0, this.activeIndex - 2);
      if (startDot + 5 > this.total) {
        startDot = this.total - 5;
      }
      this.dots.forEach((dot, idx) => {
        if (idx >= startDot && idx < startDot + 5) {
          dot.style.display = 'block';
        } else {
          dot.style.display = 'none';
        }
      });
    }

    // 5. Sync Arrow State (Desktop + Mobile buttons)
    this.prevBtns.forEach((btn) => {
      if (this.activeIndex === 0) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
      }
    });

    this.nextBtns.forEach((btn) => {
      if (this.activeIndex === this.total - 1) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
      }
    });

    // 6. Media type check for Zoom cursor & Hint
    const activeSlide = this.slides[this.activeIndex];
    const isImage = activeSlide && activeSlide.getAttribute('data-media-type') === 'image';
    if (this.viewport) {
      if (isImage) {
        this.viewport.classList.add('has-zoom');
      } else {
        this.viewport.classList.remove('has-zoom');
      }
    }
    if (this.zoomHint) {
      this.zoomHint.style.display = isImage ? '' : 'none';
    }
  }
}

  if (!customElements.get('willtec-product-gallery')) {
    customElements.define('willtec-product-gallery', WilltecProductGallery);
  }
})();
